import { IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { ImportsMonitoringItem } from 'exports/interfaces/imports-monitoring-channel.interface';
import { Observable, map, of, switchMap } from 'rxjs';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService } from 'shared/services';

@Injectable({
    providedIn: 'root',
})
export class ImportsMonitoringService implements GridService {
    private apollo: ApolloBase;
    NAME = 'ImportsMonitoringService';

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
    ) {
        this.apollo = this.apolloProvider.use('distributionManagementAPI');
    }

    sourceId = signal<string | null>(null);
    sourceId$ = toObservable(this.sourceId);

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        const orderBy = params.request.sortModel[0]?.colId ?? 'name';
        const orderDirection = params.request.sortModel[0]?.sort?.toUpperCase() ?? 'ASC';

        return this.apollo
            .query({
                query: gql`
                    query getImportTrackers($orderBy: String, $orderDirection: OrderDirection, $pageNumber: Int, $pageSize: Int) {
                        importMonitoring {
                            searchImportMonitoringTrackers(
                                importMonitoringTrackerFilter: {}
                                searchQuery: {
                                    pageNumber: $pageNumber
                                    pageSize: $pageSize
                                    orderBy: $orderBy
                                    orderDirection: $orderDirection
                                }
                            ) {
                                data {
                                    id
                                    sourceName
                                    channelId
                                    importSourceType
                                    lastImportDateOutput
                                    successRate
                                    partialSuccessRate
                                    numberOfSuccessfulItems
                                    numberOfPartialSuccessfulItems
                                    numberOfTotalItems
                                    logo
                                }
                                statusCode
                                totalCount
                            }
                        }
                    }
                `,
                variables: {
                    pageNumber,
                    pageSize,
                    orderBy,
                    orderDirection,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    const returnValue = response.data.importMonitoring.searchImportMonitoringTrackers;
                    const items: ImportsMonitoringItem[] = returnValue.data.map((element: any) => {
                        const item: ImportsMonitoringItem = {
                            id: element.id,
                            channelName: element.sourceName,
                            successRate: element.successRate,
                            partialSuccessRate: element.partialSuccessRate,
                            numberOfTotalItems: element.numberOfTotalItems,
                            numberOfSuccessfulItems: element.numberOfSuccessfulItems,
                            numberOfPartialSuccessfulItems: element.numberOfPartialSuccessfulItems,
                            numberOfFailedItems:
                                element.numberOfTotalItems - element.numberOfSuccessfulItems - element.numberOfPartialSuccessfulItems,
                            channelLogoUrl: element.logo,
                            lastImportDateOutput: element.lastImportDateOutput,
                            group: true,
                        };
                        return item;
                    });
                    return { data: items, totalCount: returnValue.totalCount };
                }),
            );
    }

    singularSourceData = toSignal<ImportsMonitoringItem | null>(
        this.sourceId$.pipe(
            switchMap((sourceId) => {
                if (!sourceId) {
                    return of(null);
                }
                return this.getOneRowBySourceId(sourceId);
            }),
        ),
    );

    public getOneRowBySourceId(sourceId: string): Observable<ImportsMonitoringItem | null> {
        if (!sourceId) {
            return of(null);
        }

        return this.apollo
            .query({
                query: gql`
                    query getImportTrackers($orderBy: String, $orderDirection: OrderDirection, $pageNumber: Int, $pageSize: Int) {
                        importMonitoring {
                            searchImportMonitoringTrackers(
                                importMonitoringTrackerFilter: { searchTerm: "${sourceId}"  }
                                searchQuery: {
                                    pageNumber: $pageNumber
                                    pageSize: $pageSize
                                    orderBy: $orderBy
                                    orderDirection: $orderDirection
                                }
                            ) {
                                data {
                                    id
                                    sourceName
                                    channelId
                                    importSourceType
                                    lastImportDateOutput
                                    successRate
                                    logo
                                }
                                statusCode
                                totalCount
                            }
                        }
                    }
                `,
                variables: {
                    pageNumber: 1,
                    pageSize: 20,
                    orderBy: 'channelName',
                    orderDirection: 'ASC',
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    const returnValue = response.data.importMonitoring.searchImportMonitoringTrackers;
                    const items: ImportsMonitoringItem[] = returnValue.data.map((element: any) => {
                        const item: ImportsMonitoringItem = {
                            id: element.id,
                            channelName: element.sourceName,
                            successRate: element.successRate,
                            partialSuccessRate: element.partialSuccessRate,
                            numberOfTotalItems: element.numberOfTotalItems,
                            numberOfSuccessfulItems: element.numberOfSuccessfulItems,
                            numberOfPartialSuccessfulItems: element.numberOfPartialSuccessfulItems,
                            numberOfFailedItems:
                                element.numberOfTotalItems - element.numberOfSuccessfulItems - element.numberOfPartialSuccessfulItems,
                            channelLogoUrl: element.logo,
                            lastImportDateOutput: element.lastImportDateOutput,
                            group: true,
                        };
                        return item;
                    });
                    return items[0];
                }),
            );
    }
}
